import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { ProjectForm } from "@/components/ProjectForm";
import { useToast } from "@/hooks/use-toast";
import { cloudflare } from "@/integrations/cloudflare/client";

interface ProjectUpdateData {
  project_name?: string;
  customer_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  budget?: string;
  timeframe?: string;
  description?: string;
}

const EditProject = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch project data
  const {
    data: project,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => {
      if (!projectId) throw new Error("Project ID is required");

      const response = await fetch(`${cloudflare.apiUrl}/api/v1/projects/${projectId}`);
      if (!response.ok) throw new Error("Failed to fetch project");

      const result = await response.json();
      return result.data;
    },
    enabled: !!projectId,
  });

  const handleProjectUpdate = async (projectId: string, projectData: ProjectUpdateData) => {
    try {
      const response = await fetch(`${cloudflare.apiUrl}/api/v1/projects/${projectId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(projectData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to update project`);
      }

      const result = await response.json();

      // Invalidate related queries to refresh cached data
      await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      await queryClient.invalidateQueries({ queryKey: ["projects"] });

      toast({
        title: "Project Updated",
        description: `Project "${projectData.project_name || projectData.customer_name || "Unnamed Project"}" has been updated successfully.`,
      });

      // Navigate back to projects list or project details
      navigate(`/projects/${projectId}`);
    } catch (error) {
      console.error("Error updating project:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to update project. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
        <Header
          onNewProject={() => navigate("/new-project")}
          onViewProjects={() => navigate("/projects")}
        />
        <main className="container mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading project details...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
        <Header
          onNewProject={() => navigate("/new-project")}
          onViewProjects={() => navigate("/projects")}
        />
        <main className="container mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center py-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Project Not Found</h2>
              <p className="text-gray-600 mb-6">
                The project you're looking for doesn't exist or has been deleted.
              </p>
              <button
                onClick={() => navigate("/projects")}
                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90"
              >
                Back to Projects
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
      <Header
        onNewProject={() => navigate("/new-project")}
        onViewProjects={() => navigate("/projects")}
      />

      <main className="container mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="max-w-5xl mx-auto">
          <ProjectForm
            onBack={() => navigate("/projects")}
            onSubmit={handleProjectUpdate}
            editingProject={project}
            isEditMode={true}
          />
        </div>
      </main>
    </div>
  );
};

export default EditProject;
